import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';
import type { OrderSubscriptionRow } from 'pkg/actions/subscriptions';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import type { CreditNoteReasonType } from 'pkg/api/models/credit_note';
import type { RefundPayload } from 'pkg/api/models/order_refund';
import { amountGoingOut } from 'pkg/api/models/order_refund/utils';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';
import { formatCentsAsCurrency } from 'pkg/i18n/formatters';

import { Event, trackEvent } from 'components/analytics';

import { formatLockTime } from 'components/payment_platform/checkout_lock';

export interface OrderPayload {
	userIds: number[];
	daysUntilDue: number;
	description?: string;
	stripeCouponId?: string;
	billingUserId?: number;
	rows: OrderSubscriptionRow[];
	discountId?: number;
	finalizeNow?: boolean;
	finalizeFirstOrderNow?: boolean;
}

export const createBatch = async (
	organizationId: number,
	payload: OrderPayload
): Promise<Response> => {
	const [request, orders, error] = await create<
		OrderPayload,
		models.order.Order[]
	>(endpoints.Orders.CreateBatch(organizationId), payload);

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: error.error,
		});

		return request;
	}

	// One event per batch, as the generic create event was. Every order in a
	// batch shares the club's currency; country is the first customer's.
	trackEvent(Event.OrderCreated, {
		country: orders[0]?.country,
		currency: orders[0]?.currency,
	});

	return request;
};

export async function refund(organizationId: number, id: number) {
	const request = await sdk.post(endpoints.Orders.Refund(organizationId, id));
	const response = await request.json();

	if (request.ok) {
		flashActions.show({
			title: t('Your refund is being processed.'),
		});

		return true;
	}

	flashActions.show({
		title: crash().title,
		message: response.error,
	});

	return false;
}

export async function sendReceipt(
	organizationId: number,
	orderIds: number[]
): Promise<boolean> {
	const request = await sdk.post(
		endpoints.Orders.SendReceipt(organizationId),
		{},
		{ ids: orderIds }
	);

	if (request.ok) {
		const multiple = orderIds.length > 1;
		flashActions.show({
			title: multiple
				? t('Receipts successfully sent!')
				: t('Receipt successfully sent!'),
		});

		return true;
	}

	flashActions.show({
		title: t('Something went wrong!'),
		message: t(
			`If you think this error isn't supposed to happen, please contact 360Player support.`
		),
	});

	return false;
}

export async function sendReminder(
	organizationId: number,
	orderIds: number[]
): Promise<boolean> {
	const request = await sdk.post(
		endpoints.Orders.SendReminder(organizationId),
		{},
		{ ids: orderIds }
	);

	if (request.ok) {
		const multiple = orderIds.length > 1;
		flashActions.show({
			title: multiple
				? t('Reminders successfully sent!')
				: t('Reminder successfully sent!'),
		});

		return true;
	}

	flashActions.show({
		title: t('Something went wrong!'),
		message: t(
			`If you think this error isn't supposed to happen, please contact 360Player support.`
		),
	});

	return false;
}

interface SkippedOrder {
	orderId: number;
	reason: string;
	lockedUntil: number;
}

interface BulkOrderResponse {
	error?: string;
	lockedUntil?: number;
	processed?: number[];
	skipped?: SkippedOrder[];
}

function showRefusal(response: BulkOrderResponse) {
	if (typeof response.lockedUntil === 'number') {
		flashActions.show({
			title: t(
				'Someone is paying this order right now. It is locked until {time}.',
				{ time: formatLockTime(response.lockedUntil) }
			),
		});

		return;
	}

	flashActions.show({
		title: crash().title,
		message: response.error,
	});
}

function skippedText(skipped: SkippedOrder[]): string {
	const time = formatLockTime(
		Math.max(...skipped.map((order) => order.lockedUntil))
	);

	return t(
		'{count, plural, one {# order was skipped because someone is paying it right now.} other {# orders were skipped because someone is paying them right now.}} Try again after {time}.',
		{ count: skipped.length, time }
	);
}

// The flash holds one message, so the skipped orders ride on the success toast rather than replacing it.
function showSuccess(successText: string, response: BulkOrderResponse) {
	const skipped = response.skipped ?? [];

	if (skipped.length === 0) {
		flashActions.show({ title: successText });
	} else {
		flashActions.show({ title: successText, message: skippedText(skipped) });
	}
}

export async function markAs(
	endpoint: string,
	payload: {
		ids: number[];
		reasonType?: CreditNoteReasonType;
	},
	successText: string
): Promise<number[]> {
	const request = await sdk.post(endpoint, {}, payload);
	const response: BulkOrderResponse = await request.json();

	if (request.ok) {
		showSuccess(successText, response);

		let type = '';

		if (endpoint.includes('/mark-paid')) {
			type = 'paid';
		} else if (endpoint.includes('/mark-uncollectible')) {
			type = 'uncollectible';
		} else if (endpoint.endsWith('/void')) {
			type = 'void';
		}

		trackEvent(Event.OrderMarkAs, { ['marked_as']: type });

		// A backend without the checkout lock answers without processed: every id went through.
		return response.processed ?? payload.ids;
	}

	showRefusal(response);

	return [];
}

export async function remove(
	organizationId: number,
	ids: number[]
): Promise<number[]> {
	const request = await sdk.destroy(
		endpoints.Orders.Index(organizationId),
		{},
		{ ids }
	);
	const response: BulkOrderResponse = await request.json();

	if (request.ok) {
		showSuccess(t('Invoice successfully deleted'), response);

		return response.processed ?? ids;
	}

	showRefusal(response);

	return [];
}

// RefundResponse lists the refunds the request started: one per chosen payment, or one per card
// payment when the whole order was refunded on an order paid more than once by card. It also says how
// many card payments a refund of the whole order skipped because a refund of them was in progress.
interface RefundResponse {
	refunds?: models.orderRefund.OrderRefund[];
	skippedInProgress?: number;
}

// skippedInProgress counts payments the caller left out of the request because a refund of them is
// still in progress.
export async function createRefund(
	order: models.order.Order,
	payload: RefundPayload = {},
	skippedInProgress = 0
) {
	// Nothing named is nothing to send: every payment left already has a refund in progress.
	if (payload.charges?.length === 0) {
		let title = t('Every payment left already has a refund in progress.');
		if (skippedInProgress > 0) {
			title = refundsInProgressMessage(skippedInProgress);
		}
		flashActions.show({ title });

		return false;
	}

	const [request, response, apiError] = await create<
		RefundPayload,
		RefundResponse
	>(endpoints.Orders.Refund(order.groupId, order.id), payload);

	if (request.ok) {
		const amount = amountGoingOut(order, payload, response?.refunds ?? []);
		const skipped = (response?.skippedInProgress ?? 0) + skippedInProgress;

		let message: string;
		if (skipped > 0) {
			message = refundsInProgressMessage(skipped);
		}

		flashActions.show({
			title: t('Your refund of {amount} is being processed.', {
				amount: formatCentsAsCurrency(amount, order.currency),
			}),
			message,
		});

		return true;
	} else {
		// The backend marks the 409s it words for the admin, such as the provider refusing the refund, a refund
		// stopping part way, or a refused second click. Any other failure shows the general message.
		flashActions.show({
			title:
				refundErrorMessage(request, apiError) || t('Something went wrong.'),
		});
	}

	return false;
}

// Matches refundConflictCode in api/cmd/server/controllers/orders/refund.go.
const refundErrorCode = 'refund_error';

function refundErrorMessage(
	request: Response,
	apiError?: models.APIError & { code?: string }
): string | undefined {
	if (request.status === 409 && apiError?.code === refundErrorCode) {
		return apiError.error;
	}
}

export function refundsInProgressMessage(count: number) {
	return t(
		'{count, plural, one {# payment still has a refund in progress; refund it once that is done.} other {# payments still have a refund in progress; refund them once those are done.}}',
		{ count }
	);
}

export interface RefundFritidskortetPayload {
	note?: string;
}

// Records a full-amount FK refund (charge lock + OrderRefund row). The
// admin wires the money to EHM out-of-band using the OCR + Bankgiro.
// Backend returns an empty 200 on success.
export async function refundFritidskortet(
	order: models.order.Order,
	payload: RefundFritidskortetPayload = {}
) {
	let request: Response;

	try {
		request = await sdk.post(
			endpoints.Orders.RefundFritidskortet(order.groupId, order.id),
			{},
			payload
		);
	} catch {
		// sdk.post rejects on network failure (offline/DNS/CORS) rather than
		// returning !ok — catch so the caller's isSaving flag clears.
		flashActions.show({
			title: t('Network error. Please try again.'),
		});

		return false;
	}

	if (request.ok) {
		flashActions.show({
			title: t('Fritidskortet refund recorded.'),
		});

		return true;
	}

	// Surface the backend's error (e.g. its 409 when another admin refunded the
	// charge first). Fall back to a generic message otherwise.
	let errorMessage = t('Something went wrong.');

	try {
		const body = await request.text();
		if (body) {
			const parsed = JSON.parse(body);

			if (parsed?.error) {
				errorMessage = parsed.error;
			}
		}
	} catch {
		// Response body wasn't JSON; keep the generic message.
	}

	flashActions.show({ title: errorMessage });

	return false;
}

export async function updateProductRecipient(
	organizationId: number,
	orderId: number,
	userId: number
): Promise<boolean> {
	const request = await sdk.post(
		endpoints.Orders.UpdateProductRecipient(organizationId, orderId),
		{},
		{ userId }
	);
	const response = await request.json();

	if (request.ok) {
		flashActions.show({
			title: t('Product recipient successfully updated.'),
		});

		return true;
	}

	flashActions.show({
		title: crash().title,
		message: response.error,
	});

	return false;
}
